import { RouteRecordRaw } from 'vue-router';
import { Role } from '../models/User';
import { pinia } from '../stores/pinia';
import { defineComponent } from 'vue';

export const Route = {
    Home: 'home',
    Games: 'games',
    Game: 'game',
    CreateGame: 'create-game',
    EditGame: 'edit-game',
    GameMasteredGames: 'game-mastered-games',
    About: 'about',
    Archive: 'archive',
    Announcements: 'announcements',
    Announcement: 'announcement',
    CreateAnnouncement: 'create-announcement',
    EditAnnouncement: 'edit-announcement',
    Profile: 'profile',
    Systems: 'systems',
    System: 'system',
    CreateSystem: 'create-system',
    EditSystem: 'edit-system',
    Admin: 'admin',
    CreateEvent: 'create-event',
    EditEvent: 'edit-event',
    Events: 'events',
    Users: 'users',
    EditUser: 'edit-user',
    Calendar: 'calendar',
    AdminGames: 'admin-games',
    Locations: 'locations',
    CreateLocation: 'create-location',
    EditLocation: 'edit-location',
    Reservations: 'reservations',
    CreateReservation: 'create-reservation',
    EditReservation: 'edit-reservation',
    Tags: 'tags',
    GamesByTag: 'games-by-tag',
    ChangePassword: 'change-password',
    GameDrafts: 'game-drafts',
    Settings: 'Settings',
    Memberships: 'memberships',
    MembershipCard: 'membership-card',
    SearchGames: 'search-games',
    AdminStats: 'admin-stats',
    AdminAuditLog: 'admin-audit-log',
    AdminWebsiteAnalytics: 'admin-website-analytics',
    AdminShirtSizes: 'admin-shirt-sizes',
    Badges: 'badges',
    BadgeHolders: 'badge-holders',
    NotFound: 'not-found',
};
export type Route = typeof Route[keyof typeof Route];

export const routes: RouteRecordRaw[] = [
    {
        name: Route.Home,
        path: '/',
        component: () => import('./Home.vue'),
    },
    {
        name: Route.Games,
        path: '/dogadaj/:eventId?',
        alias: ['/najnovije-igre/:eventId?', '/event/:eventId?'],
        component: () => import('./EventWithGames.vue'),
        meta: {
            title: 'Događaj',
        },
    },
    {
        name: Route.About,
        path: '/ona-druga-grupa',
        alias: ['/o-nama', '/about'],
        component: () => import('./About.vue'),
        meta: {
            title: 'O Nama',
            description: 'Saznajte više o ODG-u kao zajednici, te o TTRPG igrama',
        },
    },
    {
        name: Route.Game,
        path: '/igra/:gameId',
        alias: '/game/:gameId',
        component: () => import('./Game.vue'),
        meta: {
            title: 'Detalji Igre',
        },
    },
    {
        name: Route.GameDrafts,
        path: '/moji-predlosci',
        alias: '/my-drafts',
        component: () => import('./GameDrafts.vue'),
        meta: {
            title: 'Moji Predlošci',
            role: Role.GM,
        },
    },
    {
        name: Route.GameMasteredGames,
        path: '/moje-igre',
        alias: '/my-games',
        component: () => import('./GamesForGm.vue'),
        meta: {
            title: 'Moje Igre',
            role: Role.GM,
        },
    },
    {
        name: Route.Archive,
        path: '/arhiva',
        alias: '/archive',
        component: () => import('./Archive.vue'),
        meta: {
            title: 'Arhiva',
            description: 'Arhiva prethodnih događaja, sustava, te žanrova koji su se igrali kod nas.'
        },
    },
    {
        name: Route.Announcements,
        path: '/objave',
        alias: '/announcements',
        component: () => import('./Announcements.vue'),
        meta: {
            title: 'Objave',
            description: 'Vijesti i objave u našoj zajednici',
        },
    },
    {
        name: Route.Announcement,
        path: '/objava/:announcementId',
        alias: '/announcement/:announcementId',
        component: () => import('./Announcement.vue'),
        meta: {
            title: 'Detalji Objave',
        },
    },
    {
        name: Route.CreateAnnouncement,
        path: '/stvori-objavu',
        alias: '/create-announcement',
        component: () => import('./CreateAnnouncement.vue'),
        meta: {
            title: 'Nova Objava',
            role: Role.Admin,
        },
    },
    {
        name: Route.EditAnnouncement,
        path: '/uredi-objavu/:id',
        alias: '/edit-announcement/:id',
        component: () => import('./EditAnnouncement.vue'),
        meta: {
            title: 'Uredi Objavu',
            role: Role.Admin,
        },
    },
    {
        name: Route.CreateGame,
        path: '/prijavi-igru',
        alias: '/create-game',
        component: () => import('./CreateGame.vue'),
        meta: {
            title: 'Nova Igra',
            role: Role.Player,
        },
    },
    {
        name: Route.EditGame,
        path: '/uredi-igru/:gameId',
        alias: '/edit-game/:gameId',
        component: () => import('./EditGame.vue'),
        meta: {
            title: 'Uredi Igru',
            role: Role.GM,
        },
    },
    {
        name: Route.CreateReservation,
        path: '/najavi-igru',
        alias: '/create-reservation',
        component: () => import('./CreateGameReservation.vue'),
        meta: {
            title: 'Najava Igre',
            role: Role.GM,
        },
    },
    {
        name: Route.EditReservation,
        path: '/uredi-najavu/:reservationId',
        alias: '/edit-reservation/:reservationId',
        component: () => import('./EditGameReservation.vue'),
        meta: {
            title: 'Uredi Najavu',
            role: Role.GM,
        },
    },
    {
        name: Route.Reservations,
        path: '/gm-kalendar',
        alias: '/reservations',
        component: () => import('./Reservations.vue'),
        meta: {
            title: 'GM Kalendar',
            role: Role.GM,
            description: 'Kalendar igara i događaja na ODGu'
        },
    },
    {
        name: Route.Profile,
        path: '/profil-korisnika/:userId?',
        alias: '/profile/:userId?',
        component: () => import('./Profile.vue'),
        meta: {
            title: 'Profil Korisnika',
            role: Role.Player,
            description: 'Profil ODG korisnika',
        },
    },
    {
        name: Route.Systems,
        path: '/arhiva/sustavi',
        alias: '/archive/systems',
        component: () => import('./Systems.vue'),
        meta: {
            title: 'Arhiva Sustava',
            description: 'Arhiva svih TTRPG i srodnih sustava koji su se igrali na ODGu',
        },
    },
    {
        name: Route.System,
        path: '/sustav/:systemId',
        alias: '/system/:systemId',
        component: () => import('./System.vue'),
        meta: {
            title: 'Detalji Sustava',
        },
    },
    {
        name: Route.CreateSystem,
        path: '/stvori-sustav',
        alias: '/create-system',
        component: () => import('./CreateSystem.vue'),
        meta: {
            title: 'Novi Sustav',
            role: Role.GM,
        },
    },
    {
        name: Route.EditSystem,
        path: '/uredi-sustav/:systemId',
        alias: '/edit-system/:systemId',
        component: () => import('./EditSystem.vue'),
        meta: {
            title: 'Uredi Sustav',
            role: Role.GM,
        },
    },
    {
        name: Route.Admin,
        path: '/admin',
        component: () => import('./Admin.vue'),
        meta: {
            title: 'Admin Sučelje',
            role: Role.Admin,
        },
    },
    {
        name: Route.CreateEvent,
        path: '/stvori-dogadaj',
        alias: '/create-event',
        component: () => import('./CreateEvent.vue'),
        meta: {
            title: 'Novi ODG',
            role: Role.Admin,
        },
    },
    {
        name: Route.Events,
        path: '/arhiva/dogadaji',
        alias: '/archive/events',
        component: () => import('./Events.vue'),meta: {
            title: 'Dogadaji',
            description: 'Arhiva evenata u našoj zajednici',
        },
    },
    
    {
        name: Route.Users,
        path: '/admin/korisnici',
        alias: '/admin/users',
        component: () => import('./Users.vue'),
        meta: {
            title: 'Korisnici',
            role: Role.Admin,
        },
    },
    {
        name: Route.EditUser,
        path: '/uredi-korisnika/:userId',
        alias: '/edit-user/:userId',
        component: () => import('./EditUser.vue'),
        meta: {
            title: 'Uredi Podatke Korisnika',
            role: Role.Player,
        },
    },
    {
        name: Route.Calendar,
        path: '/kalendar-igara',
        alias: '/calendar',
        component: () => import('./EventCalendar.vue'),
        meta: {
            title: 'Kalendar',
            description: 'Kalendar igara i događanja u našoj zajednici',
        },
    },
    {
        name: Route.EditEvent,
        path: '/uredi-dogadaj/:eventId',
        alias: '/edit-event/:eventId',
        component: () => import('./EditEvent.vue'),
        meta: {
            title: 'Uredi ODG',
            role: Role.Admin,
        },
    },
    {
        name: Route.AdminGames,
        path: '/admin/igre',
        alias: '/admin/games',
        component: () => import('./AdminGames.vue'),
        meta: {
            title: 'Odobravanje Igara',
            role: Role.Admin,
        },
    },
    {
        name: Route.Locations,
        path: '/admin/lokacije',
        alias: '/admin/locations',
        component: () => import('./Locations.vue'),
        meta: {
            title: 'Lokacije',
            role: Role.Admin,
        },
    },
    {
        name: Route.CreateLocation,
        path: '/stvori-lokaciju',
        alias: '/create-location',
        component: () => import('./CreateLocation.vue'),
        meta: {
            title: 'Nova Lokacija',
            role: Role.Admin,
        },
    },
    {
        name: Route.EditLocation,
        path: '/uredi-lokaciju/:locationId',
        alias: '/edit-location/:locationId',
        component: () => import('./EditLocation.vue'),
        meta: {
            title: 'Uredi Lokaciju',
            role: Role.Admin,
        },
    },
    {
        name: Route.Tags,
        path: '/arhiva/zanrovi',
        alias: '/archive/tags',
        component: () => import('./Tags.vue'),
        meta: {
            title: 'Žanrovi',
            description: 'Popis žanrova koji su se igrali i vodili na ODGu',
        },
    },
    {
        name: Route.GamesByTag,
        path: '/arhiva/zanr/:tagId',
        alias: '/archive/tag/:tagId',
        component: () => import('./GamesByTag.vue'),
        meta: {
            title: 'Igre po Žanru',
            description: 'Popis igara po žanru',
        },
    },
    {
        name: Route.Settings,
        path: '/postavke',
        alias: '/settings',
        component: () => import('./Settings.vue'),
        meta: {
            title: 'Postavke',
            role: Role.Player,
        },
    },
    {
        name: Route.Badges,
        path: '/moje-znacke',
        alias: '/my-badges',
        component: () => import('./MyBadges.vue'),
        meta: {
            title: 'Moje značke',
            role: Role.Player,
            description: 'Pregled svih značaka koje možeš osvojiti na ODGu',
        },
    },
    {
        name: Route.BadgeHolders,
        path: '/znacka/:badgeKey/vlasnici',
        alias: '/badge/:badgeKey/holders',
        component: () => import('./BadgeHolders.vue'),
        meta: {
            title: 'Vlasnici značke',
            role: Role.Player,
            description: 'Popis igrača koji su osvojili odabranu značku',
        },
    },
    {
        name: Route.Memberships,
        path: '/admin/clanstva',
        alias: '/admin/memberships',
        component: () => import('./Members.vue'),
        meta: {
            title: 'Članstva',
            role: Role.Admin,
        },
    },
    {
        name: Route.MembershipCard,
        path: '/clanska-iskaznica',
        alias: '/membership-card',
        component: () => import('./MembershipCard.vue'),
        meta: {
            title: 'Članska Iskaznica',
            role: Role.Player,
        },
    },
    {
        name: Route.SearchGames,
        path: '/arhiva/igre',
        alias: '/archive/games',
        component: () => import('./SearchGames.vue'),
        meta: {
            title: 'Pretraga Igara',
            description: 'Pronađi igre koje smo odigrali',
        },
    },
    {
        name: Route.AdminStats,
        path: '/admin/statistika',
        alias: '/admin/statistics',
        component: () => import('./Stats.vue'),
        meta: {
            title: 'Statistika',
            role: Role.Admin,
        },
    },
    {
        name: Route.AdminShirtSizes,
        path: '/admin/majice',
        alias: '/admin/shirt-sizes',
        component: () => import('./ShirtSizes.vue'),
        meta: {
            title: 'Veličine majica',
            role: Role.Admin,
        },
    },
    {
        name: Route.AdminAuditLog,
        path: '/admin/revizija',
        alias: '/admin/audit-log',
        component: () => import('./AdminAuditLog.vue'),
        meta: {
            title: 'Revizijski zapis',
            role: Role.Admin,
        },
    },
    {
        name: Route.AdminWebsiteAnalytics,
        path: '/admin/web-analitika',
        alias: '/admin/web-analytics',
        component: () => import('./WebsiteAnalytics.vue'),
        meta: {
            title: 'Web analitika',
            role: Role.Admin,
        },
    },
    {
        name: Route.ChangePassword,
        path: '/promijeni-lozinku',
        alias: '/change-password',
        component: defineComponent({
            template: '<div></div>',
        }),
        meta: {
            noindex: true,
        },
        beforeEnter: async () => {
            await pinia.state.value.user?.isUserResolvedPromise;

            const user = pinia.state.value.user?.user;
            if (user == null) {
                return '/stranica-nije-pronadena';
            } else {
                return `/uredi-korisnika/${user.id}?changePassword=true`;
            }
        },
    },
    {
        name: Route.NotFound,
        path: '/:pathMatch(.*)*',
        component: () => import('./NotFound.vue'),
    },
];
